import { FloatingSearch, Messages } from "@/components/ui";
import { useDebounce } from "@/hooks/useDebounce";
import useProfile from "@/hooks/useProfile";
import { QUERY_KEYS } from "@/libraries/constant";
import {
	getMemberQueryOptions,
	getMessagesQueryOptions,
	markReadAllMessageQueryOptions,
} from "@/queries";
import {
	CHAT_DIRECT_TABS_VALUE,
	type ChatGroupSearchSchema,
	DataIdSchema,
	PostCreateMessageBodySchema,
} from "@/schemas";
import type { GroupMember } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type QueryClient,
	onlineManager,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import AppBarGroupChat from "../../-components/AppBarGroup";
import EmptyChat from "../../-components/Empty";
import PendingComponent from "./-components/PendingComponent";

export const ChatDirectTabs = [
	{
		label: "Chat",
		value: CHAT_DIRECT_TABS_VALUE.ALL,
	},
	{
		label: "Question",
		value: CHAT_DIRECT_TABS_VALUE.QUESTION,
	},
	{
		label: "Topic",
		value: CHAT_DIRECT_TABS_VALUE.TOPIC,
	},
	{
		label: "Media",
		value: CHAT_DIRECT_TABS_VALUE.MEDIA,
	},
];

export const Route = createFileRoute("/dashboard/chat/single/$id/$channelName")(
	{
		params: {
			parse: (params) => ({
				id: DataIdSchema.parse(Number(params.id)),
				channelName: z.string().parse(params.channelName),
			}),
			stringify: ({ id, channelName }) => ({ id: id.toString(), channelName }),
		},
		beforeLoad: async ({ context, params, cause }) => {
			if (cause === "enter") {
				const queryClient: QueryClient = context.queryClient;
				const memberItem = await queryClient.fetchQuery(
					getMemberQueryOptions(params.id),
				);
				if (memberItem.user.unread && params.channelName)
					await queryClient.prefetchQuery(
						markReadAllMessageQueryOptions({
							channel_name: params.channelName,
						}),
					);
			}
		},
		loaderDeps: ({ search }) => ({ tags: search.tab }),
		loader: ({ context, params, deps }) => {
			if (onlineManager.isOnline() && !context.isRestoring) {
				return context.queryClient.ensureInfiniteQueryData(
					getMessagesQueryOptions({
						channel_name: params.channelName,
						tags:
							deps.tags === CHAT_DIRECT_TABS_VALUE.ALL ? undefined : deps.tags,
					}),
				);
			}
			return context.queryClient.getQueryData([
				QUERY_KEYS.GET_MESSAGES,
				{
					channel_name: params.channelName,
					tags:
						deps.tags === CHAT_DIRECT_TABS_VALUE.ALL ? undefined : deps.tags,
				},
			]);
		},
		component: RouteComponent,
		pendingComponent: PendingComponent,
	},
);

function RouteComponent() {
	const navigate = Route.useNavigate();
	const memberId = Route.useParams({
		select: (params) => params.id,
	});
	const channelName = Route.useParams({
		select: (params) => params.channelName,
	});
	const search = Route.useSearch({
		select: (search) => search.q || "",
	});
	const tab = Route.useSearch({
		select: (search) => search.tab,
	});
	const debounceQ = useDebounce(search, 500);
	const { profile } = useProfile();
	const queryMember = useSuspenseQuery(getMemberQueryOptions(memberId));

	const form = useForm<PostCreateMessageBodySchema>({
		mode: "all",
		resolver: zodResolver(PostCreateMessageBodySchema),
		defaultValues: async () =>
			Promise.resolve({
				channel_name: channelName,
				tags: tab && tab !== "all" ? tab : null,
			}),
	});

	const onChangeTab = (val: string) => {
		return navigate({
			search: () => ({
				q: undefined,
				tab: val,
			}),
			replace: true,
		});
	};

	const onChangeSearch = (val: string) => {
		return navigate({
			search: (oldSearch: ChatGroupSearchSchema) => ({
				...oldSearch,
				q: val || undefined,
			}),
			replace: true,
		});
	};

	if (!profile) return null;

	return (
		<>
			<AppBarGroupChat
				type="single"
				id={memberId}
				title={queryMember.data.user.fullname}
				tabs={ChatDirectTabs}
				activeTab={tab}
				onChangeTab={onChangeTab}
			/>
			<div className="relative container flex gap-1 justify-end py-3">
				<FloatingSearch
					name="search"
					value={search}
					onClear={() => onChangeSearch("")}
					onChange={(e) => onChangeSearch(e.target.value)}
				/>
			</div>
			<FormProvider {...form}>
				<Messages
					module="chats"
					channelType="direct"
					channelName={channelName}
					members={[queryMember.data.user as unknown as GroupMember]}
					search={{
						search: debounceQ,
						tab,
					}}
					renderEmpty={<EmptyChat />}
				/>
			</FormProvider>
		</>
	);
}
